import React, { useContext, useState } from 'react';
import { styled } from '@mui/material/styles';
import { 
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogActions,
    TextField,
    Typography
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ExpandedDropDown from '../../dropdown/ExpandedDropDown';
import FileDropBox from '../../filedropbox/FileDropBox';
import UserContext from '../../../contexts/UserContext';
import ProjectContext from '../../../contexts/ProjectContext';
import {supportTheme} from '../../../theme';

import {
    DialogTextField,
    DialogFooter
} from './SupportDialogStyles';


export default function SupportDialogForm(props){
    const { formState, setFormState, handleSubmit, handleClose } = props
    const t = supportTheme
    const { konsolUser } = useContext(UserContext)
    const projectContext = useContext(ProjectContext)

    const requestTypes = [
        { name: 'Technical Support', value: 'support' },
        { name: 'Feature Suggestion', value: 'feature' }
    ];

    const impacts = [
        { name: 'Minor Impact', value: 'small' },
        { name: 'Significant Impact', value: 'medium' },
        { name: 'Critical / Show Stopping', value: 'large' },
    ]; 

    const onSubjectChange = (event) => {
        setFormState(prevState => ({ ...prevState, subject: event.target.value }));
    }

    const onDescriptionChange = (event) => {
        setFormState(prevState => ({ ...prevState, description: event.target.value }));
    }

    const onRequestTypeChange = (event) => {
        setFormState(prevState => ({ ...prevState, requestType: event.target.value }));
    }

    const onImpactChange = (event) => {
        setFormState(prevState => ({ ...prevState, impact: event.target.value }));
    }


    const handleFilesSelected = (newFiles) => {
        let goodNewFiles = []        
        // Check for duplicates in existing form file list
        for (let i = 0; i < newFiles.length; i++) {
            let file = newFiles[i]
            let duplicate = false
            for (let j = 0; j < formState.files.length; j++) {
                if (file.name === formState.files[j].name) {
                    duplicate = true
                    break
                }
            }
            if (!duplicate) {
                goodNewFiles.push(file)
            }
        }
  
        // Add new files to form state
        setFormState(prev => ({
            ...prev,
            files: [...(prev.files ?? []), ...goodNewFiles]
        }));
    };

    // Handle file deletion from the list of uploaded files
    // This function removes a file from the formState.files array based on its index.
    const handleFileDelete = (index) => {
        setFormState(prevState => {
            const newFiles = [...prevState.files];
            newFiles.splice(index, 1);
            return { ...prevState, files: newFiles };
        });
    }

    const renderFileList = () => {
        if (!formState.files || formState.files.length === 0) {
            return null;
        }
        
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row', 
                gap: '5px', 
                flexWrap: 'wrap', 
                alignItems: 'flex-start',
                width: '100%', 
                height: '100%',
                overflowY: 'scroll' 
            }}>
                <Chip
                    label="Remove all files"
                    variant="outlined"
                    onClick={() => setFormState(prev => ({ ...prev, files: [] }))}
                    color="error"
                    sx={{     
                        '&:hover': {
                            backgroundColor: "#f31515",
                        }
                    }}
                />
                {formState.files.map((file, index) => (
                    <Chip
                        key={`${file.name}-${index}`}
                        label={file.name}
                        onDelete={() => handleFileDelete(index)}
                        color="success"
                    />
                ))}
            </Box>
        );
    }


    return (
        <Box sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
                    <DialogContent
                    sx={{
                        color: 'rgb(175, 175, 175)',
                        padding: t.size.panelInset,
                        pb: 0,
                        gap: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        flex: '1 1 auto',
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                    >

                    <Box sx={{ 
                        ...t.component.context   
                    }}>
                        <Typography display='inline' sx={{ ...t.type.contextLabel, pr: 2 }}>From  </Typography> 
                        <Typography display='inline' sx={{ ...t.type.contextValue }}>{konsolUser?.name ?? '-'} - {formState?.userEmail ?? ''}</Typography>
                        <br/>
                        <Typography display='inline' sx={{ ...t.type.contextLabel, pr: 2  }}>Production  </Typography> 
                        <Typography display='inline' sx={{ ...t.type.contextValue }}>{projectContext?.project_name ?? '-'}</Typography>
                    </Box>

                    <Box>
                        <Typography sx={{ ...t.type.label }}>Subject*</Typography>
                        <DialogTextField
                            placeholder={formState?.requestType === 'support' ? "Short summary of the issue" : "Short summary of your suggestion"}
                            value={formState?.subject}
                            onChange={onSubjectChange}
                            fullWidth
                            inputProps={{ maxLength: 255 }} // Blocks typing after 255 characters, JSM limit for this field
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'flex-start'}}>
                        <Box sx={{ width: '35%'}}>
                            <Typography sx={{ ...t.type.label }}>Request Type</Typography>
                            <ExpandedDropDown
                                value={formState?.requestType}
                                onChange={onRequestTypeChange}
                                options={requestTypes}
                                fullWidth="100%"
                                
                                style={{
                                    backgroundColor: t.component.field.background,
                                    marginTop: '0px',
                                    '.MuiOutlinedInput-notchedOutline': {
                                        border: t.component.field.border,
                                        borderRadius: t.component.field.borderRadius,
                                     },
                                }}
                            />
                        </Box>

                        <Box sx={{ width: '65%'}}>
                            <Typography sx={{ ...t.type.label }}>Impact</Typography>
                            <ExpandedDropDown
                                disabled={formState?.requestType !== 'support'}
                                value={formState?.impact}
                                onChange={onImpactChange}
                                options={impacts}
                                fullWidth="100%"
                                style={{
                                    flexGrow: 1,
                                    backgroundColor: t.component.field.background,
                                    marginTop: '0px',
                                    '.MuiOutlinedInput-notchedOutline': {
                                        border: t.component.field.border,
                                        borderRadius: t.component.field.borderRadius,
                                     },
                                }}
                            />
                        </Box>
                    </Box>

                    <Box>
                        <Typography sx={{ ...t.type.label }}>Description*</Typography>
                        <DialogTextField
                            placeholder={
                                formState?.requestType === 'support' 
                                ? 
                                "What happened, and what were you trying to do?" 
                                : 
                                "What would you like Konsol to do?"
                            }
                            value={formState?.description}
                            onChange={onDescriptionChange}
                            multiline
                            rows={4}
                            fullWidth
                            inputProps={{ maxLength: 32767 }} // Blocks typing after 32767 characters, JSM limit for this field
                        />
                    </Box>

                    <Box>
                        <Typography sx={{ ...t.type.label }}>Attachments</Typography>
                        <FileDropBox
                            titleText={'Add files'}
                            UploadIcon={<AttachFileIcon sx={{ fontSize: 18, opacity: 1, transform: 'rotate(45deg)' }} />}
                            textOpacity={1}
                            multiple={true}
                            style = {{ 
                                width: '100%', 
                                height: '60px', 
                                flexDirection: 'row',
                                gap: 1,
                                border: '1px dashed',
                                borderColor: 'rgb(106, 119, 137)',
                                color: 'white',
                                mt: 0.2,
                                mb: 0
                            }}
                            setFile={handleFilesSelected}
                        />
                    </Box>
                    <Box sx={{ height: '100%', overflowY: 'scroll', overflowX: 'hidden' }}>
                        {renderFileList()}
                    </Box>

                </DialogContent>

                <DialogFooter>
                    <DialogActions>
                        <Button onClick={handleClose} 
                            sx={ t.component.secondaryButton }
                        >
                            Cancel
                        </Button>  
                        <Button 
                            onClick={handleSubmit} 
                            disabled={!formState.subject || !formState.description || !formState.requestType || !formState.impact || !formState.userEmail}
                            sx={t.component.primaryButton}
                        >
                            Submit Request
                        </Button>         
                    </DialogActions>
                </DialogFooter>

        </Box>
    )
}